import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetAdminAlertsQuery } from "store/api/adminApi"
import { ApiTypes, UITypes } from "types"

import { convertAdminAlertsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsAcknowledgeModal } from "modals/AlertsAcknowledgeModal"
import { AlertsFreezeModal } from "modals/AlertsFreezeModal"
import { AlertsUnfreezeModal } from "modals/AlertsUnfreezeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"
import { WidgetVendorAlerts } from "components/widgets/WidgetVendorAlerts"
import { WidgetVendorAlertStatuses } from "components/widgets/WidgetVendorAlertStatuses"

import { vendorAlertsTableHeaderCells } from "./vendorAlertsTableHeaderCells"

import styles from "./index.module.scss"

// TABLE HEADER

export const Alerts = () => {
  const [triggerGetAdminAlerts, { isFetching, data }] = useLazyGetAdminAlertsQuery()
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const { urlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false)
  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false)
  const [updateIndex, setUpdateIndex] = useState(0)
  const [isHeaderChecked, setIsHeaderChecked] = useState(false)

  useEffect(() => {
    setIsHeaderChecked(allAlertsSelected() || false)
  }, [data?.data, selectedAlerts])

  const allAlertsSelected = () => {
    if (data?.data?.length === 0) return false
    return data?.data?.every(item => selectedAlerts.findIndex(alert => alert.id === item.id) > -1)
  }

  const onSelectAlert = (id: number) => {
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      if (data?.data) {
        setSelectedAlerts([...selectedAlerts, data.data.find(item => item.id === id)!])
      }
    }
  }

  const toggleAllAlerts = () => {
    if (isHeaderChecked) {
      setSelectedAlerts([])
      setIsHeaderChecked(false)
    } else {
      setSelectedAlerts(data?.data || [])
      setIsHeaderChecked(true)
    }
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAdminAlerts(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const checkSelectedAlertsDiffer = () => {
    if (selectedAlerts.length === 0) return false
    const firstStatus = selectedAlerts[0].status
    const statusDiffer = selectedAlerts.some(alert => alert.status !== firstStatus)

    if (statusDiffer) {
      toast.error("Action cannot be applied. Please select alerts with the same status.")
      return true
    }

    return false
  }

  const onAcknowledgeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowAcknowledgeModal(true)
  }

  const onFreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowFreezeModal(true)
  }

  const onUnfreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowUnfreezeModal(true)
  }

  const handleSuccess = () => {
    setShowAcknowledgeModal(false)
    setShowFreezeModal(false)
    setShowUnfreezeModal(false)
    getAlerts()
    setSelectedAlerts([])
    setUpdateIndex(prev => prev + 1)
  }

  const getAlerts = () => {
    sendRequest(urlQueryParams)
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    getAlerts()
  }, [urlQueryParams, isFirstRender])

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetVendorAlerts updateIndex={updateIndex} />
        <WidgetVendorAlertStatuses updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Alerts History">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.tableNavButtonsWrapper}>
            <span
              className={classNames(styles.statusButton, styles.acknowledge, {
                [styles.disabledStatusButton]: selectedAlerts.length === 0,
              })}
              onClick={onAcknowledgeClick}
            >
              Acknowledge
            </span>
            <span
              className={classNames(styles.statusButton, styles.active, {
                [styles.disabledStatusButton]: selectedAlerts.length === 0,
                [styles.frozen]: selectedAlerts.some(item => item.status === "frozen"),
                [styles.active]: selectedAlerts.some(item => item.status === "active"),
              })}
              onClick={selectedAlerts.some(item => item.status === "frozen") ? onUnfreezeClick : onFreezeClick}
            >
              {selectedAlerts.some(item => item.status === "frozen") ? "Unfreeze" : "Freeze"}
            </span>
          </div>
        </nav>
        <TableWrapper
          headerCells={vendorAlertsTableHeaderCells}
          data={convertAdminAlertsToTableRows(data?.data)}
          isLoading={isFetching}
          onCheckboxChange={onSelectAlert}
          currentItemIDs={selectedAlerts.map(item => item.id)}
          onHeaderCheckboxChange={toggleAllAlerts}
          isHeaderChecked={isHeaderChecked}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showAcknowledgeModal && (
        <AlertsAcknowledgeModal
          onCancel={() => setShowAcknowledgeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showFreezeModal && (
        <AlertsFreezeModal
          onCancel={() => setShowFreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showUnfreezeModal && (
        <AlertsUnfreezeModal
          onCancel={() => setShowUnfreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
    </>
  )
}
